import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  FileText, Trophy, DollarSign, Clock, Gamepad2, Users,
  Download, AlertTriangle, ExternalLink, Loader2, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MatchReceipt = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const res = await axios.get(`${API}/matches/receipt/${matchId}`, { withCredentials: true });
        setReceipt(res.data);
      } catch (error) {
        console.error('Error fetching receipt:', error);
        toast.error('Failed to load receipt');
        navigate('/history');
      } finally {
        setLoading(false);
      }
    };
    fetchReceipt();
  }, [matchId, navigate]);

  const handleDownload = () => {
    // Create a simple text receipt for download
    if (!receipt) return;
    
    const receiptText = `
MATCHSTAKE MATCH RECEIPT
========================
Receipt ID: ${receipt.receipt_id}
Match ID: ${receipt.match_id}

GAME: ${receipt.game}

PLAYERS:
- Player A: ${receipt.player_a.name}
- Player B: ${receipt.player_b.name}

ENTRY FEE DETAILS:
- Entry Fee: ${receipt.player_a.wager.toFixed(2)} each (${(receipt.player_a.wager * 100).toLocaleString()} credits)
- Winner Reward: $${receipt.winner_payout.toFixed(2)}

RESULT:
${receipt.is_draw ? 'DRAW - Both players refunded' : `WINNER: ${receipt.winner_name}`}

STATUS: ${receipt.status.toUpperCase()}

TIMESTAMPS:
- Created: ${new Date(receipt.created_at).toLocaleString()}
${receipt.started_at ? `- Started: ${new Date(receipt.started_at).toLocaleString()}` : ''}
${receipt.completed_at ? `- Completed: ${new Date(receipt.completed_at).toLocaleString()}` : ''}

========================
Generated by MatchStake
    `.trim();

    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matchstake-receipt-${matchId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Receipt downloaded!');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!receipt) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-white/50">Receipt not found</p>
          <Button onClick={() => navigate('/history')} className="mt-4 btn-secondary">
            <ChevronLeft className="w-4 h-4 mr-2" /> Back to History
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4" data-testid="match-receipt-page">
        <Button onClick={() => navigate('/history')} variant="ghost" className="mb-4 text-white/50">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back to History
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0A0A0B] to-[#1A1A1B] p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#00F0FF]" />
                <div>
                  <h1 className="font-heading text-xl font-bold">Match Receipt</h1>
                  <p className="text-white/50 text-sm">{receipt.receipt_id}</p>
                </div>
              </div>
              <Button onClick={handleDownload} variant="outline" className="btn-secondary" data-testid="download-receipt-btn">
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Game */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#7000FF]/20 flex items-center justify-center">
                <Gamepad2 className="w-6 h-6 text-[#7000FF]" />
              </div>
              <div>
                <p className="text-white/50 text-xs uppercase">Game</p>
                <p className="font-semibold text-lg">{receipt.game}</p>
              </div>
            </div>

            {/* Players */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-white/50 text-xs mb-1">PLAYER A</p>
                <p className="font-semibold">{receipt.player_a.name}</p>
                <p className="text-sm text-white/50 mt-1">Entry Fee: ${receipt.player_a.wager.toFixed(2)}</p>
                {receipt.player_a.claimed_win !== null && (
                  <p className={`text-xs mt-1 ${receipt.player_a.claimed_win ? 'text-[#00FF94]' : 'text-[#FF2E2E]'}`}>
                    Claimed: {receipt.player_a.claimed_win ? 'Win' : 'Loss'}
                  </p>
                )}
                {receipt.player_a.proof_url && (
                  <a href={receipt.player_a.proof_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-xs flex items-center gap-1 mt-2">
                    View Proof <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-white/50 text-xs mb-1">PLAYER B</p>
                <p className="font-semibold">{receipt.player_b.name}</p>
                <p className="text-sm text-white/50 mt-1">Entry Fee: ${receipt.player_b.wager.toFixed(2)}</p>
                {receipt.player_b.claimed_win !== null && (
                  <p className={`text-xs mt-1 ${receipt.player_b.claimed_win ? 'text-[#00FF94]' : 'text-[#FF2E2E]'}`}>
                    Claimed: {receipt.player_b.claimed_win ? 'Win' : 'Loss'}
                  </p>
                )}
                {receipt.player_b.proof_url && (
                  <a href={receipt.player_b.proof_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-xs flex items-center gap-1 mt-2">
                    View Proof <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>

            {/* Financial Details */}
            <div className="bg-white/5 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/50">Entry Fee (each)</span>
                <span className="font-mono">${receipt.player_a.wager.toFixed(2)} ({(receipt.player_a.wager * 100).toLocaleString()} credits)</span>
              </div>
              <div className="border-t border-white/10 pt-3 flex justify-between items-center">
                <span className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#CCFF00]" />
                  Winner Reward
                </span>
                <span className="font-mono text-xl font-bold text-[#CCFF00]">
                  ${receipt.winner_payout.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Result */}
            <div className={`p-4 rounded-xl text-center ${
              receipt.is_draw 
                ? 'bg-[#FFB800]/10 border border-[#FFB800]/20' 
                : receipt.winner_id === user?.user_id 
                  ? 'bg-[#00FF94]/10 border border-[#00FF94]/20'
                  : 'bg-[#FF2E2E]/10 border border-[#FF2E2E]/20'
            }`}>
              {receipt.is_draw ? (
                <div>
                  <Users className="w-8 h-8 mx-auto mb-2 text-[#FFB800]" />
                  <p className="font-semibold text-[#FFB800]">Match Ended in Draw</p>
                  <p className="text-white/50 text-sm">Both players refunded</p>
                </div>
              ) : receipt.winner_name ? (
                <div>
                  <Trophy className="w-8 h-8 mx-auto mb-2 text-[#CCFF00]" />
                  <p className="text-white/50 text-sm">Winner</p>
                  <p className="font-heading text-xl font-bold text-[#CCFF00]">{receipt.winner_name}</p>
                  <p className="text-[#CCFF00]/70 text-sm">+${receipt.winner_payout.toFixed(2)}</p>
                </div>
              ) : (
                <div>
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-white/50" />
                  <p className="text-white/50">No winner declared</p>
                </div>
              )}
            </div>

            {/* Status & Timestamps */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Status</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  receipt.status === 'completed' ? 'bg-[#00FF94]/20 text-[#00FF94]' :
                  receipt.status === 'disputed' ? 'bg-[#FF2E2E]/20 text-[#FF2E2E]' :
                  'bg-white/10 text-white/50'
                }`}>{receipt.status}</span>
              </div>
              {receipt.created_at && (
                <div className="flex justify-between">
                  <span className="text-white/50">Created</span>
                  <span className="text-white/70">{new Date(receipt.created_at).toLocaleString()}</span>
                </div>
              )}
              {receipt.started_at && (
                <div className="flex justify-between">
                  <span className="text-white/50">Started</span>
                  <span className="text-white/70">{new Date(receipt.started_at).toLocaleString()}</span>
                </div>
              )}
              {receipt.completed_at && (
                <div className="flex justify-between">
                  <span className="text-white/50">Completed</span>
                  <span className="text-white/70">{new Date(receipt.completed_at).toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Admin Notes / Dispute Info */}
            {(receipt.admin_notes || receipt.dispute_reason || receipt.dispute_resolution) && (
              <div className="p-4 rounded-xl bg-white/5 space-y-2 text-sm">
                {receipt.dispute_reason && (
                  <div>
                    <span className="text-[#FF2E2E]">Dispute Reason: </span>
                    <span className="text-white/70">{receipt.dispute_reason}</span>
                  </div>
                )}
                {receipt.dispute_resolution && (
                  <div>
                    <span className="text-[#00FF94]">Resolution: </span>
                    <span className="text-white/70">{receipt.dispute_resolution}</span>
                  </div>
                )}
                {receipt.admin_notes && (
                  <div>
                    <span className="text-white/50">Admin Notes: </span>
                    <span className="text-white/70">{receipt.admin_notes}</span>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button 
                onClick={() => navigate(`/match/${matchId}`)} 
                variant="outline" 
                className="flex-1 btn-secondary"
              >
                View Match Details
              </Button>
              <Button 
                onClick={() => navigate('/history')} 
                className="flex-1 btn-primary"
              >
                Back to History
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default MatchReceipt;
